(() => {
  'use strict';

  const tagName = 'sudora-product-campaign';
  if (!window.customElements || window.customElements.get(tagName)) return;

  class SudoraProductCampaign extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this._copyVersion = (this._copyVersion || 0) + 1;
      this._copyPending = false;
      this._discount = this.querySelector('[data-campaign-discount]');
      this._percent = this.querySelector('[data-campaign-percent]');
      this._timer = this.querySelector('[data-campaign-timer]');
      this._hours = this.querySelector('[data-campaign-hours]');
      this._minutes = this.querySelector('[data-campaign-minutes]');
      this._seconds = this.querySelector('[data-campaign-seconds]');
      this._progress = this.querySelector('[data-campaign-progress]');
      this._copyButton = this.querySelector('[data-campaign-copy]');
      this._copyLabel = this.querySelector('[data-campaign-copy-label]');
      this._copyStatus = this.querySelector('[data-campaign-copy-status]');
      if (this._defaultCopyLabel == null) {
        this._defaultCopyLabel = this._copyLabel ? this._copyLabel.textContent : 'Kodu kopyala';
      }
      if (this._copyLabel) this._copyLabel.textContent = this._defaultCopyLabel;
      if (this._copyStatus) this._copyStatus.textContent = '';
      if (this._copyButton) delete this._copyButton.dataset.copyState;
      this._variants = new Map();
      this._day = null;
      this._scope = this.closest('.productView') || this.closest('.shopify-section') || this.closest('section') || document;

      const variantData = this.querySelector('script[type="application/json"][data-campaign-variants]');
      try {
        const variants = JSON.parse(variantData ? variantData.textContent : '[]');
        if (Array.isArray(variants)) {
          variants.forEach((variant) => {
            if (variant && variant.id != null) this._variants.set(String(variant.id), variant);
          });
        }
      } catch (_) {
        // Invalid or absent product data must never display an invented discount.
      }

      try {
        this._formatter = new Intl.DateTimeFormat('en-GB', {
          timeZone: 'Europe/Istanbul',
          calendar: 'gregory',
          numberingSystem: 'latn',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hourCycle: 'h23'
        });
      } catch (_) {
        this._formatter = null;
      }

      this._onChange = (event) => {
        const input = event.target;
        if (!input || !input.matches || !this._ownsNode(input)) return;
        const picker = input.closest('variant-radios, variant-selects');
        if (picker && (!picker.dataset.product || picker.dataset.product === this.dataset.productId) &&
            'currentVariant' in picker) {
          // The native picker leaves the previous form ID in place for an invalid option combination.
          this._renderVariant(picker.currentVariant ? picker.currentVariant.id : null);
          return;
        }
        if (!input.matches('input[name="id"]')) return;
        const form = input.form || input.closest('form');
        if (!form || !this._isProductForm(form.id)) return;
        this._renderVariant(input.value);
      };
      this._onVisibilityChange = () => this._syncTimer();
      this._onCopy = () => this._copyCoupon();
      document.addEventListener('change', this._onChange);
      document.addEventListener('visibilitychange', this._onVisibilityChange);
      if (this._copyButton) {
        this._copyButton.removeAttribute('aria-busy');
        this._copyButton.addEventListener('click', this._onCopy);
      }

      const productId = this.dataset.productId;
      const forms = this._scope === document
        ? [document.getElementById(`product-form-${productId}`), document.getElementById(`product-form-installment-${productId}`)]
        : Array.from(this._scope.querySelectorAll('form')).filter((form) => this._isProductForm(form.id) && this._ownsNode(form));
      forms.sort((a, b) => Number(Boolean(b && b.id === `product-form-${productId}`)) -
        Number(Boolean(a && a.id === `product-form-${productId}`)));
      const currentInput = forms
        .map((form) => form && form.querySelector('input[name="id"]'))
        .find((input) => input && input.value);
      this._renderVariant(currentInput ? currentInput.value : this.dataset.selectedVariantId);
      const picker = Array.from(this._scope.querySelectorAll('variant-radios, variant-selects'))
        .find((item) => this._ownsNode(item) && (!item.dataset.product || item.dataset.product === productId));
      if (picker && 'currentVariant' in picker && !picker.currentVariant) this._renderVariant(null);
    }

    disconnectedCallback() {
      this._connected = false;
      this._copyVersion = (this._copyVersion || 0) + 1;
      this._copyPending = false;
      this._stopTimer();
      document.removeEventListener('change', this._onChange);
      document.removeEventListener('visibilitychange', this._onVisibilityChange);
      if (this._copyButton) {
        this._copyButton.removeAttribute('aria-busy');
        this._copyButton.removeEventListener('click', this._onCopy);
      }
    }

    _isProductForm(id) {
      return id === `product-form-${this.dataset.productId}` ||
        id === `product-form-installment-${this.dataset.productId}`;
    }

    _ownsNode(node) {
      if (this._scope === document) return true;
      const scope = node.closest('.productView') || node.closest('.shopify-section') || node.closest('section');
      return scope === this._scope;
    }

    _renderVariant(id) {
      const variant = this._variants.get(String(id));
      let percentage = 0;
      if (variant && Number.isFinite(variant.price) && Number.isFinite(variant.compare_at_price) &&
          variant.price >= 0 && variant.compare_at_price > variant.price) {
        percentage = Math.round((variant.compare_at_price - variant.price) * 100 / variant.compare_at_price);
      }
      // Savings below half a percent round to zero; do not inflate them to 1%.
      this._hasDiscount = percentage > 0;
      this.dataset.selectedVariantId = id == null ? '' : String(id);
      if (this._discount) this._discount.hidden = !this._hasDiscount;
      if (this._percent) this._percent.textContent = String(percentage);
      this._syncTimer();
    }

    _stopTimer() {
      if (this._interval != null) {
        window.clearInterval(this._interval);
        this._interval = null;
      }
    }

    _syncTimer() {
      const canShowTimer = Boolean(this._hasDiscount && this._formatter && this._timer);
      if (this._timer) this._timer.hidden = !canShowTimer;
      if (!this._connected || !canShowTimer || document.hidden) {
        this._stopTimer();
        return;
      }
      this._renderTime();
      if (this._interval == null && this._formatter) {
        this._interval = window.setInterval(() => this._renderTime(), 1000);
      }
    }

    _dateParts(timestamp) {
      const parts = {};
      this._formatter.formatToParts(new Date(timestamp)).forEach((part) => {
        if (part.type !== 'literal') parts[part.type] = Number(part.value);
      });
      return parts;
    }

    _midnightInIstanbul(year, month, day) {
      const localMidnight = Date.UTC(year, month - 1, day);
      let timestamp = localMidnight;
      // Resolve this calendar date in the named timezone, independent of the visitor's timezone.
      for (let attempt = 0; attempt < 4; attempt += 1) {
        const parts = this._dateParts(timestamp);
        const represented = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
        const correction = localMidnight - represented;
        timestamp += correction;
        if (correction === 0) return timestamp;
      }
      return timestamp;
    }

    _renderTime() {
      try {
        const now = Date.now();
        const parts = this._dateParts(now);
        const key = `${parts.year}-${parts.month}-${parts.day}`;
        if (!this._day || this._day.key !== key) {
          const tomorrow = new Date(Date.UTC(parts.year, parts.month - 1, parts.day + 1));
          this._day = {
            key,
            start: this._midnightInIstanbul(parts.year, parts.month, parts.day),
            end: this._midnightInIstanbul(tomorrow.getUTCFullYear(), tomorrow.getUTCMonth() + 1, tomorrow.getUTCDate())
          };
        }
        const remainingMilliseconds = Math.max(0, this._day.end - now);
        const remainingSeconds = Math.ceil(remainingMilliseconds / 1000);
        const hours = Math.floor(remainingSeconds / 3600);
        const minutes = Math.floor(remainingSeconds % 3600 / 60);
        const seconds = remainingSeconds % 60;
        if (this._hours) this._hours.textContent = String(hours).padStart(2, '0');
        if (this._minutes) this._minutes.textContent = String(minutes).padStart(2, '0');
        if (this._seconds) this._seconds.textContent = String(seconds).padStart(2, '0');
        if (this._progress) {
          const fraction = Math.min(1, Math.max(0, remainingMilliseconds / (this._day.end - this._day.start)));
          this._progress.style.transform = `scaleX(${fraction})`;
        }
      } catch (_) {
        // A browser without timezone support keeps the factual price discount and coupon usable.
        this._formatter = null;
        if (this._timer) this._timer.hidden = true;
        this._stopTimer();
      }
    }

    async _copyCoupon() {
      if (this._copyPending || !this._copyButton) return;
      const code = this._copyButton.dataset.code;
      if (!code) return;
      this._copyPending = true;
      const version = this._copyVersion;
      this._copyButton.setAttribute('aria-busy', 'true');
      if (this._copyStatus) this._copyStatus.textContent = '';
      let copied = false;
      try {
        if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
          await navigator.clipboard.writeText(code);
          copied = true;
        }
      } catch (_) {
        // Clipboard permission can be denied; try the synchronous browser fallback below.
      }
      if (!this._connected || version !== this._copyVersion) return;
      if (!copied) {
        try {
          copied = this._copyWithSelection(code);
        } catch (_) {
          copied = false;
        }
      }
      this._copyPending = false;
      this._copyButton.removeAttribute('aria-busy');
      this._copyButton.dataset.copyState = copied ? 'success' : 'error';
      if (this._copyLabel) this._copyLabel.textContent = copied ? 'Kopyalandı' : 'Kopyalanamadı';
      if (this._copyStatus) {
        this._copyStatus.textContent = copied
          ? `${code} kupon kodu kopyalandı.`
          : `Kod kopyalanamadı. ${code} kodunu seçip elle kopyalayabilirsiniz.`;
      }
    }

    _copyWithSelection(code) {
      if (!document.body || typeof document.execCommand !== 'function') return false;
      const activeElement = document.activeElement;
      const selection = window.getSelection ? window.getSelection() : null;
      const ranges = [];
      if (selection) {
        for (let index = 0; index < selection.rangeCount; index += 1) {
          ranges.push(selection.getRangeAt(index).cloneRange());
        }
      }
      const field = document.createElement('textarea');
      field.value = code;
      field.setAttribute('readonly', '');
      field.setAttribute('aria-label', 'Kupon kodu');
      field.style.cssText = 'position:fixed;top:0;left:0;width:1px;height:1px;padding:0;opacity:0;pointer-events:none;';
      let copied = false;
      try {
        document.body.appendChild(field);
        field.focus({ preventScroll: true });
        field.select();
        field.setSelectionRange(0, code.length);
        copied = document.execCommand('copy') === true;
      } catch (_) {
        copied = false;
      } finally {
        field.remove();
        try {
          if (activeElement && typeof activeElement.focus === 'function') activeElement.focus({ preventScroll: true });
          if (selection) {
            selection.removeAllRanges();
            ranges.forEach((range) => selection.addRange(range));
          }
        } catch (_) {
          // Restoring a selection from DOM replaced by the theme must not change the copy result.
        }
      }
      return copied;
    }
  }

  window.customElements.define(tagName, SudoraProductCampaign);
})();
